import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import { useEffect, useState } from 'react'
import useSession from 'hooks/useSession'
import { useReload } from 'store'
import { useLocation } from 'react-router-dom/cjs/react-router-dom'

import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import { presetProtocolsPaginationRequestsUri } from 'controllers/Protocols/PresetProtocols/PresetProtocolsController'

import TaskHeaderErrors from 'components/TaskHeaderErrors/TaskHeaderErrors'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { EditPresetSchema } from './schemas/EditPresetSchema'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import protocolMedicalActs from '../EditProcessProtocolDialog/ProtocolMedicalActs.json'

import { useFetchList } from 'store'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import HFDropdown from 'components/HFInputs/HFDropdown'
import HFCheckbox from 'components/HFInputs/HFCheckbox'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import { useDeepCompareEffect } from 'react-use'

import {
	AppointmentProtocolRule,
	MedicalActProtocolRule,
	Options,
} from 'models'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'

import styles from './../../Dialog.module.sass'
import HFAppointmentPresetProtocol from 'components/HFInputs/HFAppointmentPresetProtocol'
import HFMedicalActsPresetProtocol from 'components/HFInputs/HFMedicalActsPresetProtocol'
import { translateErrorMessage } from 'hooks/useSubmitTask'

const EditPresetForm = (props) => {
	const { data, dismiss, resolve } = props
	const { bodyPartValue, injuryValue, acutePhaseValue } = data
	const { i18n, pushNotification } = useGlobal()
	const { logout } = useSession()
	const reload = useReload()
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState(null)
	const location = useLocation()

	const [queryParams] = useQueryParams(location)

	const pagesRequests = presetProtocolsPaginationRequestsUri(queryParams)

	const isEditMode = !!(bodyPartValue && injuryValue)

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(EditPresetSchema),
		defaultValues: {
			appointments: [
				{
					quantity: 1,
					specialtyId: '',
				},
			],
			medicalActs: [
				{
					quantity: 1,
					medicalActId: '',
				},
			],
		},
	})

	const availableProtocolMedicalActs = new Set(protocolMedicalActs)
	const { handleSubmit, formState, reset, watch } = methods
	const [bodyPartId, injuryId, acutePhase] = watch([
		'bodyPartId',
		'injuryId',
		'acutePhase',
	])

	const medicalSpecialtiesUri = '/metadata/medical_specialties'
	const medicalActsUri = '/metadata/medical_acts'
	const bodyPartsUri = '/metadata/trust_body_parts'
	const injuryTypesUri = 'metadata/injuries_types'
	const presetProtocolsUri =
		bodyPartId && injuryId
			? `/rules/protocols_presets?acutePhase=${acutePhase}&bodyPart=${bodyPartId}&injury=${injuryId}&page=0&pageSize=24`
			: null

	const {
		contents: [
			medicalSpecialties,
			medicalActs,
			bodyParts,
			injuryTypes,
			presetProtocols,
		],
	} = useFetchList(
		[
			medicalSpecialtiesUri,
			medicalActsUri,
			bodyPartsUri,
			injuryTypesUri,
			presetProtocolsUri,
		].filter(Boolean),
		[
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
			Options.fromSimpleObjectJSON,
		]
	)

	const availableProtocolMedicalActsOptions = medicalActs?.filter(
		({ text: ma }) => availableProtocolMedicalActs.has(ma.split(' ')?.[0])
	)

	const onSubmit = (formData) => {
		if (submitting) {
			return
		}
		const { appointments, medicalActs, bodyPartId, injuryId, acutePhase } =
			formData

		const bodyPartName = bodyParts.find(
			({ value }) => `${bodyPartId}` === `${value}`
		)?.text
		const injuryName = injuryTypes.find(
			({ value }) => `${injuryId}` === `${value}`
		)?.text

		const ruleName = `Automação protocolo pré-definido de parte do corpo ${bodyPartName}, tipo de lesão ${injuryName} e fase aguda ${
			acutePhase ? 'sim' : 'não'
		}`

		const endpoint = `${API_URL}/api/rules/protocols_presets`

		const filteredAppointments = appointments?.filter(
			({ specialtyId }) => specialtyId !== ''
		)

		const filteredMedicalActs = medicalActs?.filter(
			({ medicalActId }) => medicalActId !== ''
		)

		const medicalActsRequestBody =
			MedicalActProtocolRule.constructRequestBody(
				filteredMedicalActs,
				ruleName
			)

		const appointmentsRequestBody =
			AppointmentProtocolRule.constructRequestBody(
				filteredAppointments,
				ruleName
			)

		setError(null)
		setSubmitting('submitting')

		const body = {
			preset: {
				bodyPart: {
					id: bodyPartId,
					name: bodyPartName,
				},
				injury: {
					id: injuryId,
					name: injuryName,
				},
				acutePhase: acutePhase,
			},
			appointmentClinicalApproval: appointmentsRequestBody,
			medicalActsClinicalApproval: medicalActsRequestBody,
		}

		return fetch(
			endpoint,
			fetchOptions({
				method: 'PUT',
				body,
			})
		)
			.then((r) => parseJSONContent(r))
			.then(() => {
				resolve && resolve()
				reload([...pagesRequests, presetProtocolsUri])
				pushNotification(
					undefined,
					'success',
					i18n`Preset protocol updated successfully!`
				)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					logout()
				}

				const errorMessage = translateErrorMessage(e, i18n)

				setSubmitting(false)
				setError([errorMessage])
			})
	}

	useDeepCompareEffect(() => {
		let defaultValues = {
			acutePhase: acutePhase ?? acutePhaseValue ?? false,
			bodyPartId: bodyPartId ?? (bodyPartValue && `${bodyPartValue}`),
			injuryId: injuryId ?? (injuryValue && `${injuryValue}`),
			appointments: [
				{
					quantity: 1,
					specialtyId: '',
				},
			],
			medicalActs: [
				{
					quantity: 1,
					medicalActId: '',
				},
			],
		}

		const hasAppointmentProtocols =
			!!presetProtocols?.[0]?.appointmentClinicalApproval?.length
		if (hasAppointmentProtocols) {
			const appointmentsRuleValuesObj =
				AppointmentProtocolRule.retrieveRuleValues(
					presetProtocols?.[0]?.appointmentClinicalApproval
				)

			defaultValues = {
				...defaultValues,
				appointments: appointmentsRuleValuesObj,
			}
		}

		const hasMedicalActsProtocols =
			!!presetProtocols?.[0]?.medicalActsClinicalApproval?.length
		if (hasMedicalActsProtocols) {
			const medicalActsRuleValuesObj =
				MedicalActProtocolRule.retrieveRuleValues(
					presetProtocols?.[0]?.medicalActsClinicalApproval
				)

			defaultValues = {
				...defaultValues,
				medicalActs: medicalActsRuleValuesObj,
			}
		}

		reset(defaultValues)
	}, [presetProtocols || [], reset])

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
		}
	}, [formState.errors, i18n])

	return (
		<div>
			<TaskHeaderErrors errors={error ?? []} />
			<FormProvider {...methods}>
				<form
					id={'form_process_protocol_edit'}
					onSubmit={handleSubmit(onSubmit)}
					className={`${styles['protocols-form'] || ''}`}
					disabled={submitting}
				>
					<fieldset>
						<HStack
							gap='xsmall'
							align='center'
							columns={[3, 3, '110px']}
						>
							<div>
								<HFDropdown
									name={`bodyPartId`}
									disabled={isEditMode}
									options={bodyParts || []}
									placeholder={i18n`Body Part`}
									searchable={true}
									noData={i18n`No results`}
								/>
							</div>
							<div>
								<HFDropdown
									name={`injuryId`}
									disabled={isEditMode}
									options={injuryTypes || []}
									placeholder={i18n`Injury Type`}
									searchable={true}
									noData={i18n`No results`}
								/>
							</div>
							<div>
								<HFCheckbox
									className={styles['checkbox']}
									name='acutePhase'
									disabled={isEditMode}
									label={i18n`Acute Phase`}
								/>
							</div>
						</HStack>
					</fieldset>
					{bodyPartId && injuryId && (
						<VStack>
							<fieldset>
								<h4>{i18n`Appointments`}</h4>
								<HFAppointmentPresetProtocol
									name={'appointments'}
									data={{
										medicalSpecialties,
									}}
								/>
							</fieldset>

							<fieldset>
								<h4>{i18n`Medical Acts`}</h4>
								<HFMedicalActsPresetProtocol
									data={{
										medicalActs:
											availableProtocolMedicalActsOptions,
									}}
									name={'medicalActs'}
								/>
							</fieldset>
						</VStack>
					)}
				</form>
			</FormProvider>
			<menu>
				<ActionButton type='button' secondary onClick={dismiss}>
					<small>
						<strong>{i18n`Cancel`}</strong>
					</small>
				</ActionButton>
				<ActionButton form={`form_process_protocol_edit`} primary>
					<small>
						<strong>
							{submitting ? i18n`Submitting` : i18n`Save`}
						</strong>
					</small>
				</ActionButton>
			</menu>
		</div>
	)
}

export default EditPresetForm
